import type { HTMLAttributes, ReactNode } from 'react';
import React, { useState } from 'react';
import classNames from 'classnames';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import { Col, Row } from '$src-components/atoms/Grid';
import { Card } from '$src-components/atoms/Card';
import type { ButtonSizes } from '$src-components/atoms/Button';
import { VrmLookupModal } from '$quantum/molecules/VrmLookupModal';

import styles from './index.module.scss';

interface OurServicesItem {
  readonly title: ReactNode;
  readonly description: ReactNode;
  readonly cta: string;
  readonly activeTab?: string;
}

export interface OurServicesCardsProps extends HTMLAttributes<HTMLDivElement> {
  readonly heading: string;
  readonly items: OurServicesItem[];
}

export function OurServicesCards({
  heading,
  items,
  className,
  ...props
}: OurServicesCardsProps): JSX.Element {
  const [ctaSize, setCtaSize] = useState<ButtonSizes>('S');
  useResizeViewport({
    breakpoint: 'md',
    fn: (isTablet) => {
      setCtaSize(isTablet ? 'M' : 'S');
    },
  });

  return (
    <div className={classNames(OurServicesCards.displayName, styles.component, className)} {...props}>
      <h2>{heading}</h2>
      <Row className={styles.row}>
        {items.map(({ title, description, cta, activeTab }, i) => {
          const key = `item1-${i}`;
          const oddLength = !(items.length % 2);
          const oddItem = !(i % 2);
          let desktopStart: number | undefined;
          if (oddLength) desktopStart = oddItem ? 2 : undefined;

          const applyExpandEffect = items.length < 4;

          return (
            <Col key={key} large={oddLength ? 5 : 4} largeStart={desktopStart}>
              <Card
                backgroundColors="#645599"
                cornerRadius={4}
                className={classNames(styles.item, applyExpandEffect && styles.expandTransition)}
                elevation={0}
              >
                <h3>{title}</h3>
                {description}
                <div className={styles.cta}>
                  <VrmLookupModal
                    id={`OurServicesItemCta-${i}`}
                    toggleButton={{ children: cta, size: ctaSize }}
                    formProps={{ activeTab }}
                  />
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}
OurServicesCards.displayName = 'OurServicesCards';
